/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { useTheme } from '@mui/material';
import Drawer, { useDrawer } from '../../UI/Drawer';
import Divider from '../Divider';
import type { NavLink } from '.';
import { LogoPrimary } from '../../Icons';
import { SmoothMount } from '../../UI';

const MobileNavLink: React.FC<
  NavLink & {
    style?: React.CSSProperties;
  }
> = ({ Component, ...props }) => {
  const { label, to, style } = props;
  const { setDrawerOpen } = useDrawer();
  if (Component) {
    return <Component {...props} />;
  }
  return (
    <a
      className="mt-cove-50 text-cove-blue mx-auto block w-[calc(100%-50px)] align-middle text-lg leading-[70px] transition-all hover:font-bold md:w-[calc(100%-100px)]"
      id={`nav-link-to-${label.toLowerCase().replace(' ', '-')}`}
      href={to}
      style={style}
      onClick={() => {
        setDrawerOpen?.(false);
      }}
    >
      {label}
    </a>
  );
};

const MobileNav: React.FC<{
  logoLink?: React.ReactNode | string;
  navLinks: NavLink[];
  mobileNavCTA?: React.ReactNode | string;
  navLeft?: JSX.Element;
  navRight?: JSX.Element;
  cartCount?: number;
  showShopButton?: boolean;
}> = ({
  logoLink = '/',
  navLinks = [],
  mobileNavCTA,
  navLeft,
  navRight,
  cartCount,
  showShopButton = true,
  ...props
}) => {
  const theme = useTheme();
  const { md } = theme.breakpoints;

  const links = navLinks
    .map((link, index) => (
      <React.Fragment key={link.label}>
        {index !== navLinks.length - 1 && !link?.hideDivider && <Divider />}
        <MobileNavLink {...link} />
      </React.Fragment>
    ))
    .filter(l => l);

  return (
    <>
      <div
        className="flex h-[60px] w-full place-items-center justify-between bg-white"
        {...props}
      >
        <div className="flex items-center">
          <div className="flex items-center gap-5">
            {!!links?.length && (
              <SmoothMount show={!navLeft} height={false} fade mode="popLayout">
                <Drawer
                  cypressId="mobile-nav"
                  drawerWrapperCss={css`
                    padding: 0;

                    @media (max-width: ${md}px) {
                      padding: 15px 0 0;

                      br {
                        display: none;
                      }
                    }
                  `}
                  closingDrawerIconProps={{
                    className:
                      'min-w-unset text-cove-navy-blue bg-transparent w-[36px] h-[36px] absolute left-5 top-cove-10 m-0',
                  }}
                  anchor="top"
                >
                  <div className="pl-cove-75 pr-cove-25 pb-cove-15 w-full [&_svg]:w-[80px]">
                    <a href="/">
                      <LogoPrimary />
                      <span className="sr-only">
                        Go back to covesmart.com homepage
                      </span>
                    </a>
                  </div>
                  {links}
                </Drawer>
              </SmoothMount>
            )}
          </div>
          <div className="flex w-max items-center justify-between">
            <SmoothMount show={!!navLeft} height={false} fade mode="popLayout">
              {navLeft}
            </SmoothMount>
            <SmoothMount show={!navLeft} height={false} fade mode="popLayout">
              {typeof logoLink === 'string' ? (
                <a
                  href={logoLink}
                  className="w-cove-75 sm:w-cove-100 text-cove-green ml-cove-15 flex items-center lg:ml-0"
                  aria-label="Home page"
                >
                  <LogoPrimary />
                  <span className="sr-only">
                    Go back to covesmart.com homepage
                  </span>
                </a>
              ) : (
                <>{logoLink}</>
              )}
            </SmoothMount>
          </div>
        </div>
        {navRight ? (
          <>{navRight}</>
        ) : (
          mobileNavCTA && (
            <div className="flex items-center justify-center">
              <>{mobileNavCTA}</>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default MobileNav;
